import { Download } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import FilterIcon from '../../../../assets/icons/filter-icon.svg';
import { Button } from '../../../../components/button';
import { Card, CardContent } from '../../../../components/card';
import { DatePicker } from '../../../../components/date-picker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../../components/dropdown-menu';
import { Label } from '../../../../components/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/select';
import { QUICK_FILTER_OPTIONS } from '../MerchantHeatmap.const';
import { MerchantHeatmapFilters } from '../MerchantHeatmap.data';
import { getCouponDropdownOptions, getLocationDropdownOptions } from '../MerchantHeatmap.api';

interface FiltersSectionProps {
    filters: MerchantHeatmapFilters;
    onFiltersChange: (filters: MerchantHeatmapFilters) => void;
    onApplyFilters: () => void;
    onExportData: () => void;
    onExportPDF?: () => void;
    onExportXLSX?: () => void;
    onExportCSV?: () => void;
    showExport?: boolean;
    showLocation?: boolean;
    showDemographics?: boolean;
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
    filters,
    onFiltersChange,
    onApplyFilters,
    onExportData,
    onExportPDF,
    onExportXLSX,
    onExportCSV,
    showExport = true,
    showLocation = true,
    showDemographics = false,
}) => {
    const [localFilters, setLocalFilters] = useState<MerchantHeatmapFilters>(filters);
    const [locationOptions, setLocationOptions] = useState<Array<{ id: string; name: string }>>([]);
    const [couponOptions, setCouponOptions] = useState<Array<{ id: string; title?: string; name?: string }>>([]);

    useEffect(() => {
        // Fetch location dropdown options
        getLocationDropdownOptions().then(setLocationOptions).catch(console.error);
        // Fetch coupon dropdown options
        getCouponDropdownOptions().then(setCouponOptions).catch(console.error);
    }, []);

    const handleFilterChange = (key: keyof MerchantHeatmapFilters, value: any) => {
        const updated = { ...localFilters, [key]: value };
        setLocalFilters(updated);
        onFiltersChange(updated);
    };

    const handleLocationChange = (locationId: string) => {
        const updated = { 
            ...localFilters, 
            location: locationId === 'all' ? undefined : locationId,
            location_ids: locationId === 'all' ? undefined : [locationId]
        };
        setLocalFilters(updated);
        onFiltersChange(updated);
    };

    const handleCouponChange = (couponId: string) => {
        const updated = { 
            ...localFilters, 
            coupon_name: couponId === 'all' ? undefined : couponId,
            coupon_ids: couponId === 'all' ? undefined : [couponId]
        };
        setLocalFilters(updated);
        onFiltersChange(updated);
    };

    const handleDateChange = (field: 'start_date' | 'end_date', date: Date | undefined) => {
        handleFilterChange(field, date ? date.toISOString() : undefined);
    };

    return (
        <Card className="border border-[#f6f6f8] dark:border-gray-700 rounded-[12.13px] bg-white dark:bg-gray-800">
            <CardContent className="p-[1.6px]">
                <div className="flex items-start justify-end px-[19.5px] py-[15px]">
                    <div className="flex flex-1 gap-[6.5px] items-center">
                        <img src={FilterIcon} alt="Filter" className="w-[16.25px] h-[16.25px]" />
                        <h4 className="text-[13px] font-semibold text-[#364153] dark:text-gray-300 leading-[13px]" style={{ fontFamily: 'Nunito' }}>
                            Filters & Data Controls
                        </h4>
                    </div>
                    {showExport && (
                        <div className="flex flex-1 justify-end">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        className="bg-[#00d6a8] hover:bg-[#00c49a] text-[#004232] text-[11.4px] font-bold leading-[16.25px] px-[15px] py-[6px] rounded-[6.13px] gap-[7px]"
                                        style={{ fontFamily: 'Nunito' }}
                                    >
                                        <Download className="w-[13px] h-[13px]" />
                                        Export Data
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-48">
                                    <DropdownMenuItem onClick={onExportPDF || (() => {
                                        const url = `/merchant-heatmap-pdf`;
                                        window.open(url, "_blank");
                                    })}>
                                        Export as PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={onExportXLSX || onExportData}>
                                        Export as xlsx
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={onExportCSV || onExportData}>
                                        Export as csv
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-[19.5px] pb-[19.5px] pt-0 px-[19.5px]">
                    <div className="flex gap-[30px] items-end justify-center">
                        {/* Location (Premium) */}
                        {showLocation && (
                            <div className="flex-1 flex flex-col gap-[6.5px]">
                                <Label className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px] pl-0 pr-4" style={{ fontFamily: 'Nunito' }}>
                                    Location
                                </Label>
                                <Select
                                    value={localFilters.location || 'all'}
                                    onValueChange={handleLocationChange}
                                >
                                    <SelectTrigger className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[11.4px] bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300" style={{ fontFamily: 'Nunito' }}>
                                        <SelectValue placeholder="All Locations" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-gray-800">
                                        <SelectItem value="all">All Locations</SelectItem>
                                        {locationOptions.map((location) => (
                                            <SelectItem key={location.id} value={location.id}>
                                                {location.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Demographics (Premium) */}
                        {showDemographics && (
                            <div className="flex-1 flex flex-col gap-[6.5px] pt-[1.5px]">
                                <Label className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                    Demographics
                                </Label>
                                <Select
                                    value={localFilters.demographics || 'all'}
                                    onValueChange={(value) => handleFilterChange('demographics', value === 'all' ? undefined : value)}
                                >
                                    <SelectTrigger className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[11.4px] bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300" style={{ fontFamily: 'Nunito' }}>
                                        <SelectValue placeholder="All Users" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white dark:bg-gray-800">
                                        <SelectItem value="all">All Users</SelectItem>
                                        <SelectItem value="ages_18_34">Ages 18-34</SelectItem>
                                        <SelectItem value="ages_35_54">Ages 35-54</SelectItem>
                                        <SelectItem value="ages_55_plus">Ages 55+</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Coupon Name */}
                        <div className="flex-1 flex flex-col gap-[6.5px] pt-[1.5px]">
                            <Label className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Coupon Name
                            </Label>
                            <Select
                                value={localFilters.coupon_name || 'all'}
                                onValueChange={handleCouponChange}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[11.4px] bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue placeholder="All Coupons" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-gray-800">
                                    <SelectItem value="all">All Coupons</SelectItem>
                                    {couponOptions.map((coupon) => (
                                        <SelectItem key={coupon.id} value={coupon.id}>
                                            {coupon.title || coupon.name || 'Untitled Coupon'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Date Range */}
                        <div className="flex flex-col gap-[6.5px] w-[396.54px]">
                            <div className="flex items-center">
                                <Label className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px] pr-4" style={{ fontFamily: 'Nunito' }}>
                                    Date Range
                                </Label>
                            </div>
                            <div className="flex gap-[6.5px] items-center">
                                <div className="flex-1 relative" style={{ fontFamily: 'Nunito' }}>
                                    <DatePicker
                                        value={localFilters.start_date ? new Date(localFilters.start_date) : undefined}
                                        onChange={(date) => handleDateChange('start_date', date)}
                                        placeholder="Start date"
                                        dateFormat="MMM d"
                                        className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[9.8px] font-medium bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300"
                                        placeholderIconColor="#00c49a"
                                        hideIcon={false}
                                    />
                                </div>
                                <div className="text-[13px] text-[#99a1af] dark:text-gray-500 leading-[19.5px]" style={{ fontFamily: 'Nunito' }}>-</div>
                                <div className="flex-1 relative" style={{ fontFamily: 'Nunito' }}>
                                    <DatePicker
                                        value={localFilters.end_date ? new Date(localFilters.end_date) : undefined}
                                        onChange={(date) => handleDateChange('end_date', date)}
                                        placeholder="End date"
                                        dateFormat="MMM d"
                                        minDate={localFilters.start_date ? new Date(localFilters.start_date) : undefined}
                                        className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[9.8px] font-medium bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300"
                                        placeholderIconColor="#00c49a"
                                        hideIcon={false}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quick Filters */}
                        <div className="flex-1 flex flex-col gap-[6.5px]">
                            <Label className="text-[11.4px] font-medium text-[#364153] dark:text-gray-300 leading-[16.25px]" style={{ fontFamily: 'Nunito' }}>
                                Quick Filters
                            </Label>
                            <Select
                                value={localFilters.quick_filter || 'last_7_days'}
                                onValueChange={(value) => handleFilterChange('quick_filter', value as any)}
                            >
                                <SelectTrigger className="h-[29.25px] border border-[#b5babe] dark:border-gray-600 rounded-[6.13px] px-[11.35px] py-[8.1px] text-[11.4px] bg-white dark:bg-gray-700 text-[#364153] dark:text-gray-300" style={{ fontFamily: 'Nunito' }}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-gray-800">
                                    {QUICK_FILTER_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Apply Filters Button */}
                        <Button
                            onClick={onApplyFilters}
                            className="bg-[#00d6a8] hover:bg-[#00c49a] text-[#004232] text-[11.4px] font-bold leading-[16.25px] px-[15px] py-[6px] rounded-[6.13px] shrink-0"
                            style={{ fontFamily: 'Nunito' }}
                        >
                            Apply Filters
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

